import api from './api';

class ProposalService {
  // Get proposals from MongoDB database
  async getProposalsFromDB() {
    try {
      const response = await api.get('/proposals/db/list');
      return response.data;
    } catch (error) {
      console.error('Error fetching proposals from DB:', error);
      throw error;
    }
  }

  // Get proposals from in-memory storage (backward compatibility)
  async getProposalsFromMemory() {
    try {
      const response = await api.get('/proposals');
      return response.data;
    } catch (error) {
      console.error('Error fetching proposals from memory:', error);
      throw error;
    }
  }

  // Get all proposals (combines both sources)
  async getAllProposals() {
    try {
      const [dbProposals, memoryProposals] = await Promise.allSettled([
        this.getProposalsFromDB(),
        this.getProposalsFromMemory()
      ]);

      const proposals = [];

      // Add DB proposals
      if (dbProposals.status === 'fulfilled' && dbProposals.value.proposals) {
        proposals.push(...dbProposals.value.proposals.map(p => ({
          ...p,
          source: 'mongodb'
        })));
      }

      // Add memory proposals (avoid duplicates)
      if (memoryProposals.status === 'fulfilled' && memoryProposals.value) {
        const dbClientNames = new Set(proposals.map(p => p.client_name));
        memoryProposals.value.forEach(p => {
          if (!dbClientNames.has(p.clientName)) {
            proposals.push({
              ...p,
              source: 'memory',
              download_urls: {
                pdf: `/api/proposals/download/${p.clientName}_Training_Proposal.pdf?format=pdf`,
                txt: `/api/proposals/download/${p.clientName}_Training_Proposal.txt?format=txt`,
                md: `/api/proposals/download/${p.clientName}_Training_Proposal?format=docx`,
                pptx: `/api/proposals/download/${p.clientName}_Training_Proposal.pptx?format=pptx`
              }
            });
          }
        });
      }

      // Sort by creation date (newest first)
      proposals.sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
        const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
        return dateB - dateA;
      });

      return {
        proposals,
        total: proposals.length,
        sources: {
          mongodb: dbProposals.status === 'fulfilled' ? dbProposals.value.proposals?.length || 0 : 0,
          memory: memoryProposals.status === 'fulfilled' ? memoryProposals.value?.length || 0 : 0
        }
      };
    } catch (error) {
      console.error('Error fetching all proposals:', error);
      throw error;
    }
  }

  // Get proposal by ID
  async getProposalById(proposalId) {
    try {
      const response = await api.get(`/proposals/${proposalId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal by ID:', error);
      throw error;
    }
  }

  // Create new proposal
  async createProposal(proposalData) {
    try {
      const response = await api.post('/proposals/', proposalData);
      return response.data;
    } catch (error) {
      console.error('Error creating proposal:', error);
      throw error;
    }
  }

  // Update proposal
  async updateProposal(proposalId, updateData) {
    try {
      const response = await api.put(`/proposals/${proposalId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating proposal:', error);
      throw error;
    }
  }

  // Delete proposal
  async deleteProposal(proposalId) {
    try {
      const response = await api.delete(`/proposals/${proposalId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting proposal:', error);
      throw error;
    }
  }

  // Search proposals
  async searchProposals(query, skip = 0, limit = 20) {
    try {
      const response = await api.get('/proposals/search/', {
        params: { q: query, skip, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching proposals:', error);
      throw error;
    }
  }

  // Get proposal statistics
  async getProposalStats() {
    try {
      const response = await api.get('/proposals/stats/summary');
      return response.data;
    } catch (error) {
      console.error('Error fetching proposal stats:', error);
      throw error;
    }
  }

  // Download proposal file
  async downloadProposal(filename, format = 'pdf') {
    try {
      const response = await api.get(`/proposals/download/${filename}`, {
        params: { format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading proposal:', error);
      throw error;
    }
  }
}

export default new ProposalService(); 